import React, { useCallback, useEffect, useMemo } from "react";
import { Form } from "react-final-form";
import classNames from "classnames";
import { FieldArray } from "react-final-form-arrays";
import arrayMutators from "final-form-arrays";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { get } from "lodash";
import EntityDialog from "rs-components/EntityDialog";
import InputSet from "./components/InputSet";
import Output from "./components/Output";
import Input from "rs-components/Input";
import Button, { ButtonSize, ButtonStyle } from "components/Button";
import InfoIconWithTooltip from "components/Tooltips/InfoIconWithTooltip";
import SelectInput from "rs-components/Input/components/SelectInput";
import SelectItemRenderer from "rs-components/Input/components/SelectInput/components/SelectItemRenderer";
import LearnMoreLink from "rs-components/LearnMoreLink";
import { HELP_SYSTEM_URL, HelpId } from "shared/helpSystemBindings";
import { processVariablesSelector } from "store/global/global.selectors";
import { addInsight, cancelOutdatedPreview, editInsight, getInsightDefaultName, resetInsightDefaultName, setCurrentInsight, setInsightDialogOpen, setInsightExpandedInputSet, } from "store/rs-workspace/actions/rs-workspace.actions";
import { insightDefaultNameSelector, insightDialogInitialValuesSelector, persistedInsightsDataSelector, previewRequestsSelector, incompleteSimulationsIdsSelector, workspaceCurrentInsightIdSelector, workspaceSimulationsSelector, workspaceCellModelsSelector, workspaceRecipesSelector, } from "store/rs-workspace/selectors/rs-workspace.selectors";
import { GetRestrictionsMethod, RequestTemplate } from "store/knowledge-base-item/knowledge-base-item.types";
import { isEditableSelector, restrictionsSelector, } from "store/knowledge-base-item/knowledge-base-item.selectors";
import { getRestrictions } from "store/knowledge-base-item/knowledge-base-item.actions";
import { defaultInputSetInput, getDefaultTimeFrame } from "store/rs-workspace/utils/scenario.utils";
import { getInsightSourcesIds, getInsightUpdatedInputsPerSetFormData, getInsightDialogSubmitPayload, getInsightNodeScoresData, getInsightLatestScoreData, InsightDialogTestIds, checkScoreErrored, } from "store/rs-workspace/utils/insights.utils";
import { CancelOutdatedPreviewType, InsightOutputTypes, } from "store/rs-workspace/rs-workspace.types";
import { getFieldRestrictions, getRequestName } from "store/knowledge-base-item/knowledge-base-item.utils";
import { RsWorkspaceEntityType } from "shared/types";
import { InsightLabels } from "shared/labels";
import { someFormFieldModified } from "shared/validators";
import { RecipeScalingApiEndpoints } from "api/api-endpoints";
import { resetNestedFieldsTouchedMutator } from "utils/final-form-mutators";
import { isInsightCompleted } from "store/rs-workspace/utils/completeness.utils";
import { getNextMaxId } from "utils/data";
import * as styles from "./styles.module.scss";
const MAX_INPUT_SETS = 5;
export var InsightFormFields;
(function (InsightFormFields) {
    // common
    InsightFormFields["id"] = "id";
    InsightFormFields["name"] = "name";
    InsightFormFields["value"] = "value";
    InsightFormFields["units"] = "units";
    InsightFormFields["state"] = "state";
    // inputs
    InsightFormFields["input"] = "input";
    InsightFormFields["inputCount"] = "inputCount";
    InsightFormFields["inputSets"] = "inputSets";
    InsightFormFields["inputType"] = "inputType";
    InsightFormFields["inputs"] = "inputs";
    InsightFormFields["inputId"] = "inputId";
    InsightFormFields["adjustmentId"] = "adjustmentId";
    InsightFormFields["cutInfo"] = "cutInfo";
    // outputs
    InsightFormFields["outputs"] = "outputs";
    InsightFormFields["type"] = "type";
    InsightFormFields["sourceA"] = "sourceA";
    InsightFormFields["sourceB"] = "sourceB";
    InsightFormFields["cutOperation"] = "cutOperation";
    InsightFormFields["processVariable"] = "processVariable";
    InsightFormFields["timeFrame"] = "timeFrame";
    InsightFormFields["variableType"] = "variableType";
    InsightFormFields["customVariableId"] = "customVariableId";
    InsightFormFields["recipeId"] = "recipeId";
    InsightFormFields["transformationOperation"] = "transformationOperation";
    InsightFormFields["weight"] = "weight";
    InsightFormFields["advancedEnabled"] = "advancedEnabled";
    InsightFormFields["advanced"] = "advanced";
    InsightFormFields["robustness"] = "robustness";
    InsightFormFields["reliability"] = "reliability";
    // operation
    InsightFormFields["operation"] = "operation";
    InsightFormFields["operationType"] = "operationType";
    InsightFormFields["timeWindow"] = "timeWindow";
    InsightFormFields["parameters"] = "parameters";
    InsightFormFields["parameterType"] = "parameterType";
    InsightFormFields["preProcessingEnabled"] = "preProcessingEnabled";
    InsightFormFields["preProcessingType"] = "preProcessingType";
    // completeness
    InsightFormFields["isComplete"] = "isComplete";
})(InsightFormFields || (InsightFormFields = {}));
const getRestrictionsPayload = (post) => ({
    rsEndpoint: RecipeScalingApiEndpoints.workspaces,
    template: post ? RequestTemplate.INSIGHT_POST : RequestTemplate.INSIGHT_PUT,
    method: post ? GetRestrictionsMethod.POST : GetRestrictionsMethod.PUT,
});
const InsightDialog = () => {
    const dispatch = useDispatch();
    const { id: workspaceId } = useParams();
    const editable = useSelector(isEditableSelector);
    const insightDefaultName = useSelector(insightDefaultNameSelector);
    const currentInsightId = useSelector(workspaceCurrentInsightIdSelector);
    const previewRequests = useSelector(previewRequestsSelector);
    const processVariables = useSelector(processVariablesSelector);
    const persistedInsightsData = useSelector(persistedInsightsDataSelector);
    const restrictions = useSelector(restrictionsSelector(getRestrictionsPayload(currentInsightId === null)));
    const initialValues = useSelector(insightDialogInitialValuesSelector);
    const incompleteSimulationsIds = useSelector(incompleteSimulationsIdsSelector);
    const simulations = useSelector(workspaceSimulationsSelector);
    const cellModels = useSelector(workspaceCellModelsSelector);
    const recipes = useSelector(workspaceRecipesSelector);
    const inputRestrictions = useMemo(() => getFieldRestrictions(restrictions, [getRequestName(InsightFormFields.state), InsightFormFields.input]), [restrictions]);
    const outputsRestrictions = useMemo(() => getFieldRestrictions(restrictions, [
        getRequestName(InsightFormFields.state),
        InsightFormFields.outputs,
    ]), [restrictions]);
    const preparedInitialValues = useMemo(() => ({
        ...initialValues,
        [InsightFormFields.outputs]: (initialValues?.[InsightFormFields.outputs] ?? []).map((output) => ({
            ...output,
            [InsightFormFields.timeFrame]: output?.[InsightFormFields.timeFrame] ??
                getDefaultTimeFrame(getFieldRestrictions(outputsRestrictions, [
                    output?.[InsightFormFields.type] ?? InsightOutputTypes.Evaluation,
                ])),
        })),
    }), [initialValues, outputsRestrictions]);
    const getInsightStatuses = useCallback((inputSets, outputs) => {
        const nodeScoreData = getInsightNodeScoresData(persistedInsightsData, inputSets, outputs, currentInsightId ?? -1, previewRequests, processVariables, incompleteSimulationsIds, simulations, cellModels, recipes);
        const insightScoreData = getInsightLatestScoreData(persistedInsightsData, currentInsightId);
        const someScorePending = nodeScoreData?.some((item) => item.outputScores?.some((score) => score.pending));
        const someScoreErrored = insightScoreData?.scores.some(({ score }) => checkScoreErrored(score));
        const insightCompleted = isInsightCompleted({ input: { inputSets }, outputs }, processVariables, incompleteSimulationsIds, simulations, cellModels, recipes);
        return {
            entityCompleted: !someScorePending && insightCompleted,
            entityErrored: !someScorePending && someScoreErrored,
        };
    }, [
        persistedInsightsData,
        currentInsightId,
        previewRequests,
        processVariables,
        incompleteSimulationsIds,
        simulations,
        cellModels,
        recipes,
    ]);
    const isInsightEntityCompleted = useCallback((values) => {
        const outputs = get(values, InsightFormFields.outputs)?.map((output) => ({
            ...output,
            sourceA: output?.sourceA?.value,
            sourceB: output?.sourceB?.value,
        }));
        const inputSets = get(values, InsightFormFields.inputSets)?.map((inputSet) => ({
            ...inputSet,
            inputs: inputSet?.inputs?.map((inputSetInput) => ({
                ...inputSetInput,
                inputId: inputSetInput?.inputId?.value,
            })),
        }));
        if (!outputs || !inputSets) {
            return false;
        }
        const insightState = { input: { inputSets }, outputs };
        return isInsightCompleted(insightState, processVariables, incompleteSimulationsIds, simulations, cellModels, recipes);
    }, [incompleteSimulationsIds, processVariables, simulations, cellModels, recipes]);
    const getInputsPerSetOptions = useCallback((values) => {
        const outputs = get(values, InsightFormFields.outputs);
        const inputSets = get(values, InsightFormFields.inputSets);
        const allSourcesIds = getInsightSourcesIds(outputs);
        let minInputsPerSet = 1;
        inputSets.forEach((item) => {
            const inputsSelectedAsSources = item.inputs.filter((inputItem) => allSourcesIds.includes(inputItem.id));
            if (inputsSelectedAsSources.length > 0) {
                minInputsPerSet = inputsSelectedAsSources.length;
            }
        });
        return [1, 2, 3].map((value) => ({
            value,
            label: `${value} input${value !== 1 ? "s" : ""} per set`,
            disabled: value < minInputsPerSet,
            className: classNames({ [styles.disabledOption]: value < minInputsPerSet }),
        }));
    }, []);
    const onAddInsight = useCallback((values) => {
        const payload = getInsightDialogSubmitPayload(values, parseInt(workspaceId), insightDefaultName);
        if (currentInsightId !== null) {
            dispatch(editInsight({ ...payload, insightId: currentInsightId }));
        }
        else {
            dispatch(addInsight(payload));
        }
    }, [dispatch, workspaceId, currentInsightId, insightDefaultName]);
    const onDialogClose = useCallback((form) => {
        const formRenderProps = form.getState();
        const formModified = someFormFieldModified(formRenderProps);
        dispatch(setInsightDialogOpen(false));
        dispatch(resetInsightDefaultName());
        dispatch(setCurrentInsight(null));
        if (formModified) {
            dispatch(cancelOutdatedPreview({
                previewType: CancelOutdatedPreviewType.Insight,
                elementId: currentInsightId ?? -1,
            }));
        }
        form.change(InsightFormFields.name, null);
    }, [currentInsightId, dispatch]);
    const onDialogOpening = useCallback(() => {
        if (currentInsightId === null) {
            dispatch(getInsightDefaultName(parseInt(workspaceId)));
        }
    }, [dispatch, workspaceId, currentInsightId]);
    const onAddInputSet = useCallback((form, values) => {
        const nextInputSetId = getNextMaxId(values[InsightFormFields.inputSets]);
        const inputs = Array(values[InsightFormFields.inputCount] ?? 0)
            .fill(defaultInputSetInput)
            .map((item, index) => ({ ...item, [InsightFormFields.id]: index + 1 }));
        form.mutators.push(InsightFormFields.inputSets, { inputs, [InsightFormFields.id]: nextInputSetId });
        dispatch(setInsightExpandedInputSet({
            insightId: currentInsightId,
            inputSetIndex: values[InsightFormFields.inputSets].length,
        }));
    }, [dispatch, currentInsightId]);
    const onInputsPerSetChange = useCallback((newValue, prevValue, form, values) => {
        const currentInputSets = values[InsightFormFields.inputSets];
        const currentOutputs = values[InsightFormFields.outputs];
        if (prevValue !== newValue) {
            const { inputSetsData, outputsData } = getInsightUpdatedInputsPerSetFormData(currentInputSets, prevValue, newValue, currentOutputs);
            form.batch(() => {
                form.change(InsightFormFields.inputCount, newValue);
                form.change(InsightFormFields.inputSets, inputSetsData);
                form.change(InsightFormFields.outputs, outputsData);
            });
        }
    }, []);
    useEffect(() => {
        !restrictions && dispatch(getRestrictions(getRestrictionsPayload(currentInsightId === null)));
    }, [restrictions, dispatch, currentInsightId]);
    return (React.createElement(Form, { onSubmit: onAddInsight, initialValues: preparedInitialValues, mutators: {
            ...arrayMutators,
            resetNestedFieldsTouchedMutator,
        }, validateOnBlur: true, keepDirtyOnReinitialize: true, render: ({ handleSubmit, form, values, ...renderProps }) => (React.createElement(EntityDialog, { nameFieldName: InsightFormFields.name, nameFieldRestrictions: getFieldRestrictions(restrictions, [getRequestName(InsightFormFields.name)]), entityType: RsWorkspaceEntityType.Insight, isOpen: true, onDialogClose: () => onDialogClose(form), onDialogOpening: onDialogOpening, onClosing: async () => await form.reset(), entityCompleted: isInsightEntityCompleted(values), entityErrored: isInsightEntityCompleted(values) &&
                getInsightStatuses(get(values, InsightFormFields.inputSets), get(values, InsightFormFields.outputs)).entityErrored, handleSubmit: handleSubmit, formRenderProps: renderProps, selectedId: currentInsightId ?? undefined },
            React.createElement("div", { className: styles.body },
                React.createElement("div", { className: styles.inputsSection },
                    React.createElement("div", { className: styles.inputsPerSet, "data-testid": InsightDialogTestIds.InputsPerSet },
                        React.createElement(Input, { name: InsightFormFields.inputCount, component: SelectInput, items: getInputsPerSetOptions(values), placeholder: "Select", itemRenderer: SelectItemRenderer, disableFormChange: true, autoFocus: true, onChange: ({ value }, prevValue) => onInputsPerSetChange(value, prevValue, form, values), restrictions: getFieldRestrictions(inputRestrictions, [InsightFormFields.inputCount]), classes: {
                                container: styles.selectWrapper,
                                component: styles.select,
                            }, popoverProps: {
                                usePortal: false,
                            }, selectButtonProps: {
                                testId: InsightDialogTestIds.InputsPerSetButton,
                            }, readonly: !editable }),
                        React.createElement("div", { className: styles.tooltip },
                            React.createElement(InfoIconWithTooltip, { classes: {
                                    className: styles["info-icon"],
                                }, tooltipText: InsightLabels.INPUTS_PER_SET_HINT }))),
                    React.createElement(FieldArray, { name: `${InsightFormFields.inputSets}` }, ({ fields }) => fields.map((name) => React.createElement(InputSet, { name: name, key: name, restrictions: inputRestrictions }))),
                    editable && (React.createElement(Button, { onClick: () => onAddInputSet(form, values), classes: { root: styles["add-set"] }, label: "Add set", role: "button", style: ButtonStyle.Tertiary, size: ButtonSize.Normal, disabled: values[InsightFormFields.inputSets].length >= MAX_INPUT_SETS, title: values[InsightFormFields.inputSets].length >= MAX_INPUT_SETS
                            ? `Maximum number of Input Sets is ${MAX_INPUT_SETS}.`
                            : "", showTooltipIfDisabled: true, testId: InsightDialogTestIds.AddInputSetButton })),
                    React.createElement(LearnMoreLink, { className: styles["learn-more-link"], url: `${HELP_SYSTEM_URL}#cshid=${HelpId.INSIGHTS}` })),
                React.createElement("div", { className: styles.outputs },
                    React.createElement(FieldArray, { name: `${InsightFormFields.outputs}` }, ({ fields }) => fields.map((name, index) => (React.createElement(Output, { index: index, name: name, key: name, restrictions: outputsRestrictions })))))))) }));
};
export default InsightDialog;
